/**
 * **Les vidéos jalons** — l'écran d'où on les joue (§9.3, PLAN §4.13).
 *
 * ── Ce que cet en-tête disait hier, et pourquoi il ne le dit plus ──────────────
 *
 * Il justifiait l'absence de bouton : *« `applyAction` la range dans son `default:` … jouer un
 * jalon aujourd'hui coûterait une journée entière et 45 ⚡ pour ne rien produire »*. C'était
 * vrai, et ça ne l'est **plus depuis M3.1** : `validateVideoJalon` teste les conditions,
 * `effetActionJalon` est câblé dans `engine/index.ts`, `jouerJalon` applique le pourcentage
 * et crédite les PC. L'étiquette « stub » affichée au joueur était donc
 * devenue le seul mensonge de l'écran — et le jeu perdait son **unique mécanisme exponentiel**
 * (PLAN §4.4 : « on n'atteint pas le million en publiant, on l'atteint par les leviers »).
 *
 * ── Ce que l'écran ne calcule jamais ──────────────────────────────────────────
 *
 * La **verdict de jouabilité vient du moteur** : `jalonsLisibles(state, contenu)` rend, par
 * jalon, `fait` (= tenté) / `reussi` / `jouable` / `manque`. C'est exactement ce que son
 * docstring annonce (« le client doit pouvoir écrire *il te manque Motion design et 10 000
 * abonnés* sans re-dériver la règle »). `manques()` ci-dessous ne fait donc plus que **mettre
 * en français** — elle ne décide plus rien.
 *
 * ── Deux divergences spec/code que cet écran RACONTE au lieu de les corriger ───
 *
 * Elles appartiennent à Mathieu (elles vivent dans `shared/`, pas ici) :
 *  1. le pourcentage est **tiré au hasard** dans la fourchette du contenu (`draw('jalon_params')`)
 *     là où la spec décrit une interpolation sur le score. Le score, lui, ne décide que du
 *     franchissement de `SEUIL_JALON_REUSSI` ;
 *  2. ce seuil vaut **60** dans le code quand la spec dit 30.
 * L'écran dit ce que le code fait, jamais ce que la spec voudrait : un écran qui promettrait
 * « mieux tu joues, plus tu gagnes » mentirait à chaque tirage.
 *
 * ## ⟦C57 · lot 5b · A-174⟧ Moins de texte : une fiche, c'est un nom, un effet, un coût, un bouton
 *
 * Mesuré avant le chantier : 239 mots visibles (471 en faisant défiler), dont les deux grands blocs
 * d'introduction (63 et 59 mots) et la phrase de 33 mots qui ouvre l'écran. À l'écran : le nombre de
 * jalons et la règle en deux lignes (réussi dès 60, raté : aucun PC, effet divisé par 2), puis par
 * fiche le nom, l'effet en pourcentage, le coût, le bouton et l'état. **Tout le texte d'avant est
 * derrière un seul « Les dessous », mot pour mot** : l'introduction, « Ce que ça coûte, et ce que ça
 * coûte de rater », la ligne d'une fiche. Rien n'est supprimé ni réécrit ; `nom_court` (à côté de
 * `nom`, dans `content/milestones.json`) ne sert qu'aux deux titres qui dépassent la règle.
 */

import { useNavigate } from "react-router-dom";

import {
  H_JOUR,
  H_VIDEO_JALON,
  JALON_EFFET_RATE,
  PC_PAR_JALON,
  SEUIL_JALON_REUSSI,
  actionCost,
  flagPose,
  jalonsLisibles,
  type AtelierScores,
  type Milestone,
} from "@maxime/shared";

import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { Panneau } from "../../components/Panneau";
import { clientDeJeu } from "../../game/client";
import { contenu } from "../../game/content";
import { jourSimule, libelleCout, nombre, points } from "../../game/format";
import { useAtelierStore } from "../../stores/atelierStore";
import { useRunStore } from "../../stores/runStore";

/** Scores neutres : sert uniquement à demander au moteur le coût de l'action (§2.2). */
const SCORES_VIDES: AtelierScores = {
  ecriture: 0,
  tournage: 0,
  montage: 0,
  miniature: 0,
  promotion: 0,
  delegated: [],
};

/** Une durée dite en jours simulés — dérivée de `H_JOUR`, jamais recopiée ⟦A-19⟧. */
function enJours(heures: number): number {
  return Math.round(heures / H_JOUR);
}

export function Jalons() {
  const run = useRunStore((s) => s.run);
  const pending = useRunStore((s) => s.pending);
  const demarrer = useAtelierStore((s) => s.demarrer);
  const naviguer = useNavigate();

  if (!run) return null;
  const state = run.state;
  const c = contenu();

  /** Le verdict du moteur, par id — c'est lui qui autorise ou refuse, pas cet écran. */
  const lectures = new Map(jalonsLisibles(state, c).map((l) => [l.id, l]));
  const jalons = [...c.milestones].sort((a, b) => a.ordre - b.ordre);

  /** Ce que le transport actif sait faire — pas une règle, une capacité (cf. NouvelleVideo). */
  const productionOuverte = clientDeJeu().capacites.production;
  /** Un événement en attente bloque toute autre action (§2.3) : inutile d'ouvrir l'Atelier. */
  const evenementEnAttente = Boolean(state.pending_event);

  /**
   * Le coût, **demandé au moteur** (§2.2) et jamais recopié. Il ne dépend pas de *quel* jalon —
   * la table le facture au type d'action —, d'où l'id vide : c'est la seule façon d'écrire « le
   * coût d'un jalon » sans laisser croire qu'il varie.
   */
  const cout = actionCost({ type: "video_jalon", jalon_id: "", scores: SCORES_VIDES }, state);

  /** Ce qui manque à ce jalon, dit en français. Vide = les conditions sont réunies. */
  function manques(jalon: Milestone): string[] {
    const liste: string[] = [];
    // ⚠️ Le palier se lit comme le moteur le lit (`conditionsManquantes`) : franchi s'il est
    // **dans l'historique** OU si les abonnés y sont. Une chaîne qui redescend ne reperd pas
    // l'accès à un jalon — et sans ce `||`, l'écran aurait écrit « il te manque 100 abonnés »
    // sous un bouton que le moteur, lui, acceptait.
    const palierOuvert =
      state.subscribers >= jalon.conditions.palier_min ||
      // `Palier` est une union fermée ; `palier_min` est un entier de contenu. Même
      // élargissement que le moteur (`conditionsManquantes`), pour la même raison.
      (state.milestones_reached as readonly number[]).includes(jalon.conditions.palier_min);
    if (!palierOuvert) {
      liste.push(`${nombre(jalon.conditions.palier_min)} abonnés`);
    }
    for (const skill of jalon.conditions.skills) {
      if (!state.skills.includes(skill)) liste.push(c.parSkill.get(skill)?.nom ?? skill);
    }
    for (const flag of jalon.conditions.flags_requis) {
      // ⚠️ Cet écran lisait `state.flags` en vue élargie, en croyant faire « comme le moteur ».
      // Il faisait **autre chose** : le moteur passe par `flagPose`, qui lit **trois** sources —
      // les drapeaux dérivés (`FLAGS_MOTEUR_DERIVES`), `state.flags`, puis les drapeaux **de
      // contenu** posés par les effets `flag_add`. Or `arc_bad_buzz` est précisément un drapeau
      // de contenu : il n'est jamais écrit dans `state.flags`, donc la condition ne pouvait
      // **jamais** être satisfaite.
      //
      // Ce que le joueur voyait : sur la vidéo d'excuses, le bouton « 🎬 Produire » actif — le
      // moteur, lui, savait — et juste dessous « Il te manque : avoir vécu « arc bad buzz ». »
      // Il en concluait que le bouton ne marcherait pas, et passait à côté de **l'une des trois
      // sorties de l'arc de la chute** ⟦PLAN §4.11⟧.
      //
      // `EvenementModal` lisait déjà correctement, par `flagPose`. Le désaccord entre deux
      // écrans du même dépôt était le seul indice, et personne ne l'avait vu.
      if (!flagPose(state, flag)) {
        liste.push(`avoir vécu « ${flag.replace(/_/g, " ")} »`);
      }
    }
    if (jalon.conditions.image_min !== undefined && state.channel_image < jalon.conditions.image_min) {
      liste.push(`image de chaîne ≥ ${jalon.conditions.image_min}`);
    }
    // ⟦A-123 · C9 vague 0⟧ Les cinq extensions du catalogue — même moteur de vérité que
    // `conditionsManquantes` (`engine/jalons.ts`), traduites en français ici, jamais recalculées.
    if (
      jalon.conditions.videos_published_min !== undefined &&
      state.counters.videos_published < jalon.conditions.videos_published_min
    ) {
      liste.push(`${nombre(jalon.conditions.videos_published_min)} vidéos publiées`);
    }
    if (jalon.conditions.money_min !== undefined && state.money < jalon.conditions.money_min) {
      liste.push(`${nombre(jalon.conditions.money_min)} ₵ en poche`);
    }
    if (jalon.conditions.reputation_min !== undefined && state.reputation < jalon.conditions.reputation_min) {
      liste.push(`réputation ≥ ${jalon.conditions.reputation_min}`);
    }
    if (jalon.conditions.clock_h_min !== undefined && state.clock_h < jalon.conditions.clock_h_min) {
      liste.push(`${nombre(jourSimule(jalon.conditions.clock_h_min))} jours de chaîne`);
    }
    if (
      jalon.conditions.jalon_precedent !== undefined &&
      !state.jalons_done.includes(jalon.conditions.jalon_precedent)
    ) {
      const precedent = jalons.find((j) => j.id === jalon.conditions.jalon_precedent);
      liste.push(`avoir réussi « ${precedent?.nom ?? jalon.conditions.jalon_precedent} »`);
    }
    return liste;
  }

  /**
   * Ouvre l'Atelier **en mode jalon** : les cinq étapes d'une longue, puis `video_jalon` au
   * lieu de `produce_video`. Rien n'est dépensé ici — le coût tombe à la publication, comme
   * pour une vidéo ordinaire (c'est ce que dit « Jeter le projet »).
   */
  function jouer(jalon: Milestone) {
    demarrer({
      // `Milestone.format` est l'enum fermé `"longue"` : les 5 étapes sont requises côté
      // serveur (`verifierFormeAtelier`), on ne choisit donc rien ici.
      format: jalon.format,
      // Un jalon n'a pas de sujet — voir `Brouillon.jalonId`.
      subjectId: "",
      evergreenIntent: false,
      extraCardSubjects: [],
      productionValue: 0,
      jalonId: jalon.id,
    });
    naviguer("/atelier");
  }

  return (
    <Panneau titre="🏆 Les vidéos jalons" large>
      {/* ⟦C57 · A-174⟧ Le compte et la règle en deux lignes ; tout le reste est dans UN « Les dessous ». */}
      <p className="ligne-info l5-entete">
        <span>
          {/* ⟦A-123 · C9 vague 0⟧ Le compte se LIT du contenu, jamais recopié : « Onze » a fini
              par mentir dès la fiche suivante. */}
          <strong>{nombre(jalons.length)}</strong> jalons : effets en % de tes abonnés.
        </span>
        <LesDessous titre="Les vidéos jalons">
          <DessousBloc>
            <p className="dessous-texte">
              <strong>{nombre(jalons.length)}</strong> vidéos qui ne se publient pas comme les
              autres : la première face cam, la vidéo d'excuses, le Documentaire, la spéciale 1M, la
              réponse aux haters, et d'autres au fil de ta progression. Leurs effets s'expriment en{" "}
              <strong>pourcentage de tes abonnés du moment</strong> — elles accélèrent une chaîne qui
              marche, elles ne sauvent pas une chaîne qui ne décolle pas.
            </p>
          </DessousBloc>

          {/* Ce qu'on risque, avant de cliquer. Tous les chiffres viennent de `@maxime/shared`. */}
          <DessousBloc titre="⚠️ Ce que ça coûte, et ce que ça coûte de rater">
            <p className="dessous-texte">
              Un jalon se produit comme une longue : <strong>les cinq étapes de l'Atelier</strong>,
              pour {libelleCout(cout)} — soit {enJours(H_VIDEO_JALON)} jours simulés.
            </p>
            <p className="dessous-texte">
              Il est <strong>réussi à partir de {SEUIL_JALON_REUSSI}</strong> de score d'exécution
              (la moyenne de tes cinq étapes, plafonnée par ton niveau). En dessous :{" "}
              <strong>aucun point</strong>, l'effet est{" "}
              <strong>divisé par {Math.round(1 / JALON_EFFET_RATE)}</strong>, les{" "}
              {enJours(H_VIDEO_JALON)} jours sont passés quand même, et{" "}
              <strong>le jalon est consommé</strong> ⟦A-22⟧ : on ne refait pas sa première face cam
              trois fois.
            </p>
            <p className="dessous-texte">
              Le pourcentage est <strong>tiré au hasard</strong> dans la fourchette du jalon : ton
              score décide de la réussite, pas du montant. Réussi, un jalon rapporte aussi{" "}
              {points(PC_PAR_JALON)} de progression, plus ce que le contenu du jalon accorde.
            </p>
          </DessousBloc>

          <DessousBloc titre="Une fiche">
            <p className="dessous-texte">Vidéo longue · 5 étapes · à partir du palier d'abonnés indiqué.</p>
          </DessousBloc>

          {productionOuverte ? null : (
            <DessousBloc titre="Ce transport">
              <p className="dessous-texte">
                Ce client ne sait pas produire sur ce transport : l'Atelier ne peut pas aller
                chercher la partition du tour. Les jalons restent consultables.
              </p>
            </DessousBloc>
          )}
        </LesDessous>
      </p>

      <section className="carte">
        <h2 className="bloc-titre">⚠️ Ce que ça coûte</h2>
        <p className="ligne-info">
          Réussi dès <strong>{SEUIL_JALON_REUSSI}</strong> de score.
        </p>
        <p className="ligne-info">
          Raté : <strong>aucun point</strong>, effet <strong>÷{Math.round(1 / JALON_EFFET_RATE)}</strong>,
          jalon consommé.
        </p>
      </section>

      {productionOuverte ? null : (
        <p className="etiquette etiquette-bac">Production indisponible ici.</p>
      )}

      <ul className="noeuds">
        {jalons.map((jalon) => {
          const lecture = lectures.get(jalon.id);
          const restant = manques(jalon);
          // ⟦A-22⟧ `fait` = **tenté** (réussi ou raté) : dans les deux cas le jalon est passé.
          const fait = lecture?.fait ?? false;
          const reussi = lecture?.reussi ?? false;
          // ⚠️ Le moteur a le dernier mot : `jouable` couvre aussi le déjà-tenté, que
          // `manques()` ne connaît pas.
          const jouable = (lecture?.jouable ?? false) && productionOuverte && !evenementEnAttente;
          return (
            <li
              className={`noeud ${fait ? "possede" : jouable ? "achetable" : "refuse"}`}
              key={jalon.id}
            >
              <div className="noeud-tete">
                <span className="noeud-nom">
                  {jalon.ordre}. {jalon.nom_court ?? jalon.nom}
                </span>
                <span className="noeud-tier">
                  +{jalon.effets_reussite.subs_pct_min}–{jalon.effets_reussite.subs_pct_max} %
                </span>
              </div>
              {/* ⟦C57 · A-174⟧ Le palier seul ; la phrase d'avant (« Vidéo longue · 5 étapes · à partir
                  de N abonnés. ») est au survol et dans les dessous. */}
              <p
                className="noeud-desc"
                title={`Vidéo longue · 5 étapes · à partir de ${nombre(jalon.conditions.palier_min)} abonnés.`}
              >
                Dès {nombre(jalon.conditions.palier_min)} abonnés
              </p>
              <div className="noeud-pied">
                <span className="noeud-cout">{libelleCout(cout)}</span>
                {fait ? (
                  <span className="noeud-etat">{reussi ? "faite ✔" : "tentée ✖"}</span>
                ) : (
                  <button
                    className="bouton"
                    type="button"
                    disabled={!jouable || pending}
                    onClick={() => jouer(jalon)}
                  >
                    🎬 Produire
                  </button>
                )}
              </div>
              {/* Ce qui manque : un cadenas et la liste (la phrase d'avant, « Il te manque : … », est au survol). */}
              <p
                className="noeud-refus"
                title={!fait && restant.length > 0 ? `Il te manque : ${restant.join(", ")}.` : undefined}
              >
                {fait
                  ? reussi
                    ? "Une vidéo jalon ne se joue qu'une fois."
                    : "Ratée — et une vidéo jalon ne se joue qu'une fois."
                  : restant.length > 0
                    ? `🔒 ${restant.join(", ")}`
                    : evenementEnAttente
                      ? "Règle d'abord l'événement en cours."
                      : `Conditions réunies : vise ${SEUIL_JALON_REUSSI}.`}
              </p>
            </li>
          );
        })}
      </ul>
    </Panneau>
  );
}
